// Factory function pattern to avoid Webpack TDZ issues with class inheritance
// This uses BaseApiServiceFactory instead of class inheritance

import { createBaseApiService, type IBaseApiService } from "@Front/Api/BaseApiServiceFactory";
import { createFilesFormData } from "@Front/Api/BaseApiServiceFactory/helpers/formDataRequestHelpers";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import type { Document } from "le-coffre-resources/dist/Customer";

interface ThirdPartyDocumentsInstance {
	getDocuments(folderUid: string): Promise<Document[]>;
	uploadDocument(documentUid: string, file: File): Promise<Document>;
	uploadDocuments(documentUid: string, files: File[]): Promise<Document>;
}

// Module-level singleton instance
let instance: ThirdPartyDocumentsInstance | null = null;
let baseApiInstance: IBaseApiService | null = null;

async function thirdPartyGetDocuments(baseApi: IBaseApiService, namespaceUrl: string, folderUid: string): Promise<Document[]> {
	const url = new URL(namespaceUrl.concat(`/documents/${folderUid}`));
	try {
		return await baseApi.getRequest<Document[]>(url);
	} catch (err) {
		baseApi.onError(err);
		return Promise.reject(err);
	}
}

async function thirdPartyUploadDocument(baseApi: IBaseApiService, namespaceUrl: string, documentUid: string, file: File): Promise<Document> {
	const url = new URL(namespaceUrl.concat(`/documents/${documentUid}/upload`));
	if (!file || !(file instanceof File)) throw new Error("Invalid file object");
	if (file.size === 0) throw new Error("File is empty");
	const formData = createFilesFormData(file);
	LoggerService.info("📤 [ThirdPartyDocuments] Uploading file", {
		documentUid,
		fileName: file.name,
		fileSize: file.size,
		fileType: file.type,
		url: url.toString(),
		formDataHasFile: formData.has("files"),
	});
	const fileEntry = formData.get("files");
	if (!fileEntry || !(fileEntry instanceof File)) {
		LoggerService.error("❌ [ThirdPartyDocuments] File not found in FormData", {
			fileEntryType: typeof fileEntry,
		});
		throw new Error("File not properly added to FormData");
	}
	try {
		return await baseApi.postRequestFormData<Document>(url, formData);
	} catch (err) {
		baseApi.onError(err);
		return Promise.reject(err);
	}
}

async function thirdPartyUploadDocuments(baseApi: IBaseApiService, namespaceUrl: string, documentUid: string, files: File[]): Promise<Document> {
	const url = new URL(namespaceUrl.concat(`/documents/${documentUid}/upload`));
	if (!files?.length) throw new Error("No files provided");
	for (const file of files) {
		if (!file || !(file instanceof File)) throw new Error("Invalid file object");
		if (file.size === 0) throw new Error("File is empty");
	}
	const formData = createFilesFormData(files);
	LoggerService.info("📤 [ThirdPartyDocuments] Uploading multiple files", {
		documentUid,
		filesCount: files.length,
		fileNames: files.map((f) => f.name),
		totalSize: files.reduce((sum, f) => sum + f.size, 0),
		url: url.toString(),
		formDataHasFiles: formData.has("files"),
	});
	try {
		return await baseApi.postRequestFormData<Document>(url, formData);
	} catch (err) {
		baseApi.onError(err);
		return Promise.reject(err);
	}
}

function createThirdPartyDocuments(): ThirdPartyDocumentsInstance {
	if (!baseApiInstance) {
		baseApiInstance = createBaseApiService();
	}
	const baseApi = baseApiInstance;
	const namespaceUrl = baseApi.getBaseUrl().concat("/third-party");
	return {
		getDocuments: (folderUid: string) => thirdPartyGetDocuments(baseApi, namespaceUrl, folderUid),
		uploadDocument: (documentUid: string, file: File) => thirdPartyUploadDocument(baseApi, namespaceUrl, documentUid, file),
		uploadDocuments: (documentUid: string, files: File[]) => thirdPartyUploadDocuments(baseApi, namespaceUrl, documentUid, files),
	};
}

export function getThirdPartyDocumentsInstance(): ThirdPartyDocumentsInstance {
	if (!instance) {
		instance = createThirdPartyDocuments();
	}
	return instance;
}
